import { useEffect, useRef, useState } from "react";
import type { FormEvent } from "react";
import { Link, NavLink, useNavigate } from "react-router-dom";
import { genres } from "@/mocks/comics";

const navLinks = [
  { to: "/", label: "首页" },
  { to: "/category", label: "分类" },
  { to: "/about", label: "关于" },
];

export default function Navbar() {
  const navigate = useNavigate();
  const [keyword, setKeyword] = useState("");
  const [genreOpen, setGenreOpen] = useState(false);
  const [mobileOpen, setMobileOpen] = useState(false);
  const [scrolled, setScrolled] = useState(false);
  const genreRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 20);
    onScroll();
    window.addEventListener("scroll", onScroll);
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  useEffect(() => {
    const onClick = (event: MouseEvent) => {
      if (genreRef.current && !genreRef.current.contains(event.target as Node)) {
        setGenreOpen(false);
      }
    };
    document.addEventListener("mousedown", onClick);
    return () => document.removeEventListener("mousedown", onClick);
  }, []);

  const submitSearch = (event: FormEvent) => {
    event.preventDefault();
    const value = keyword.trim();
    navigate(value ? `/search?keyword=${encodeURIComponent(value)}` : "/search");
    setMobileOpen(false);
  };

  return (
    <header
      className={`sticky top-0 z-50 w-full transition-colors duration-300 ${
        scrolled
          ? "bg-background-50/95 backdrop-blur border-b border-background-200"
          : "bg-transparent"
      }`}
    >
      <div className="w-full px-4 md:px-6 h-16 flex items-center gap-4">
        <Link to="/" className="flex items-center gap-2 shrink-0 group">
          <span className="w-9 h-9 rounded-lg bg-primary-500 text-background-50 flex items-center justify-center animate-fade-in">
            <i className="ri-book-open-line text-xl" />
          </span>
          <span className="font-heading font-extrabold text-lg tracking-tight text-foreground-950">
            JM<span className="text-primary-500">comic2</span>
          </span>
        </Link>

        <nav className="hidden md:flex items-center gap-1 ml-4">
          {navLinks.map((link) => (
            <NavLink
              key={link.to}
              to={link.to}
              className={({ isActive }) =>
                `px-3 py-2 rounded-md text-sm font-medium transition-colors whitespace-nowrap ${
                  isActive
                    ? "text-primary-500 bg-primary-100/60"
                    : "text-foreground-700 hover:text-foreground-950 hover:bg-background-100"
                }`
              }
            >
              {link.label}
            </NavLink>
          ))}

          <div className="relative" ref={genreRef}>
            <button
              type="button"
              onClick={() => setGenreOpen((open) => !open)}
              className="px-3 py-2 rounded-md text-sm font-medium text-foreground-700 hover:text-foreground-950 hover:bg-background-100 transition-colors flex items-center gap-1 cursor-pointer whitespace-nowrap"
            >
              题材
              <i className={`ri-arrow-down-s-line transition-transform ${genreOpen ? "rotate-180" : ""}`} />
            </button>
            {genreOpen && (
              <div className="absolute left-0 top-full mt-2 w-64 p-2 rounded-lg bg-background-100 border border-background-200 grid grid-cols-2 gap-1 animate-fade-in">
                {genres.map((genre) => (
                  <Link
                    key={genre}
                    to={`/category?genre=${encodeURIComponent(genre)}`}
                    onClick={() => setGenreOpen(false)}
                    className="px-3 py-2 rounded-md text-sm text-foreground-700 hover:text-primary-500 hover:bg-background-200/70 transition-colors flex items-center gap-2"
                  >
                    <i className="ri-price-tag-3-line text-primary-500" />
                    {genre}
                  </Link>
                ))}
              </div>
            )}
          </div>
        </nav>

        <form onSubmit={submitSearch} className="hidden md:flex items-center ml-auto">
          <div className="flex items-center gap-2 h-10 px-3 rounded-full bg-background-100 border border-background-200 focus-within:border-primary-400 transition-colors w-56 lg:w-72">
            <i className="ri-search-line text-foreground-500 text-base" />
            <input
              value={keyword}
              onChange={(event) => setKeyword(event.target.value)}
              placeholder="搜索漫画标题或简介"
              className="flex-1 bg-transparent text-sm text-foreground-950 placeholder:text-foreground-500 outline-none"
            />
            <button
              type="submit"
              className="text-xs px-2.5 py-1 rounded-full bg-primary-500 text-background-50 hover:bg-primary-600 transition-colors cursor-pointer whitespace-nowrap"
            >
              搜索
            </button>
          </div>
        </form>

        <button
          type="button"
          onClick={() => setMobileOpen((open) => !open)}
          className="md:hidden ml-auto w-10 h-10 flex items-center justify-center rounded-md text-foreground-950 hover:bg-background-100 transition-colors cursor-pointer"
          aria-label="菜单"
        >
          <i className={mobileOpen ? "ri-close-line text-2xl" : "ri-menu-line text-2xl"} />
        </button>
      </div>

      {mobileOpen && (
        <div className="md:hidden bg-background-100 border-t border-background-200 px-4 py-4 animate-fade-in">
          <form onSubmit={submitSearch} className="flex items-center gap-2 h-11 px-3 rounded-full bg-background-50 border border-background-200 mb-4">
            <i className="ri-search-line text-foreground-500 text-base" />
            <input
              value={keyword}
              onChange={(event) => setKeyword(event.target.value)}
              placeholder="搜索漫画标题或简介"
              className="flex-1 bg-transparent text-sm text-foreground-950 placeholder:text-foreground-500 outline-none"
            />
            <button type="submit" className="text-xs px-3 py-1.5 rounded-full bg-primary-500 text-background-50 cursor-pointer whitespace-nowrap">
              搜索
            </button>
          </form>
          <div className="flex flex-col gap-1 mb-4">
            {navLinks.map((link) => (
              <NavLink
                key={link.to}
                to={link.to}
                onClick={() => setMobileOpen(false)}
                className={({ isActive }) =>
                  `px-3 py-2.5 rounded-md text-sm font-medium transition-colors ${
                    isActive ? "text-primary-500 bg-primary-100/60" : "text-foreground-700 hover:bg-background-200"
                  }`
                }
              >
                {link.label}
              </NavLink>
            ))}
          </div>
          <p className="px-3 text-xs text-foreground-500 mb-2">题材分类</p>
          <div className="grid grid-cols-3 gap-2">
            {genres.map((genre) => (
              <Link
                key={genre}
                to={`/category?genre=${encodeURIComponent(genre)}`}
                onClick={() => setMobileOpen(false)}
                className="px-2 py-2 rounded-md text-center text-sm text-foreground-700 bg-background-50 border border-background-200 hover:text-primary-500 transition-colors"
              >
                {genre}
              </Link>
            ))}
          </div>
        </div>
      )}
    </header>
  );
}